import { T, useT } from '../i18n'
import { carrierBrand } from '../lib/carriers'
import { type LaneRow, cleanPort, fmtPrice, laneSlug } from '../lib/rates'

const fmtDay = (s: string | null) => (s ? new Date(s).toLocaleDateString('en-CA') : null) // YYYY-MM-DD, like the source cards

// Carrier badge — a brand-coloured code chip (owned, trademark-safe; no hotlinked third-party logos).
function CarrierMark({ code }: { code: string | null }) {
  const b = carrierBrand(code)
  return (
    <span
      className="mono flex h-7 items-center justify-center rounded-md px-2 text-xs font-semibold tracking-wide"
      style={{ background: b.bg, color: b.fg }}
    >
      {code ?? '—'}
    </span>
  )
}

/** A readable, clickable rate card: carrier · POL→transit→POD · vessel/voyage/service · 20/40/40HC. */
export function RateCard({ lane }: { lane: LaneRow }) {
  const t = useT()
  return (
    <T
      to={`/rates/${laneSlug(lane.pol_code, lane.pod_code)}`}
      className="card lift reveal-scroll group block p-4"
    >
      <div className="flex items-center justify-between">
        <CarrierMark code={lane.carrier} />
        <span className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] uppercase text-faint">
          {lane.is_via ? t('card.transship') : t('card.direct')}
        </span>
      </div>

      {/* POL → transit → POD */}
      <div className="mt-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-ink">
            {cleanPort(lane.pol_name, lane.pol_code)}
          </div>
          <div className="text-xs text-muted tnum">{fmtDay(lane.etd) ?? lane.pol_code}</div>
        </div>
        <div className="flex flex-col items-center pt-1">
          <div className="text-[11px] text-muted whitespace-nowrap">
            {lane.transit_days ? `${lane.transit_days} ${t('card.days')}` : '—'}
          </div>
          <div className="mt-1 flex items-center">
            <span className="h-1.5 w-1.5 rounded-full bg-signal/50" />
            <span className="h-px w-10 bg-border-strong sm:w-14" />
            <span className="h-1.5 w-1.5 rounded-full bg-signal" />
          </div>
        </div>
        <div className="min-w-0 flex-1 text-right">
          <div className="truncate font-medium text-ink">
            {cleanPort(lane.pod_name, lane.pod_code)}
          </div>
          <div className="text-xs text-muted tnum">{fmtDay(lane.eta) ?? lane.pod_code}</div>
        </div>
      </div>

      {/* vessel / voyage / service */}
      <div className="mt-3 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted">
        {lane.vessel && (
          <span className="truncate">
            {t('card.vessel')} <span className="text-ink">{lane.vessel}</span>
          </span>
        )}
        {lane.voyage && (
          <span>
            {t('card.voyage')} <span className="text-ink">{lane.voyage}</span>
          </span>
        )}
        {lane.route_code && (
          <span className="truncate">
            {t('card.service')} <span className="text-ink">{lane.route_code}</span>
          </span>
        )}
      </div>

      {/* prices */}
      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-border pt-3 text-center">
        {(
          [
            ['20GP', lane.price_20gp],
            ['40GP', lane.price_40gp],
            ['40HC', lane.price_40hc],
          ] as const
        ).map(([label, val]) => (
          <div key={label}>
            <div className="mono text-[10px] uppercase tracking-wide text-faint">{label}</div>
            <div className="mono text-base tnum font-semibold text-signal">{fmtPrice(val)}</div>
          </div>
        ))}
      </div>
    </T>
  )
}
